import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';

const firstFile = (event) => event.target.files?.[0] ?? null;

/**
 * Step 1 — File (design read §3.4, contract §5 q6): one JKS keystore, or the PEM set
 * (certificate, key, optional chain). Nothing is sent in this step.
 *
 * @param {{
 *   keyAlias: string,
 *   onKeyAliasChange: (value: string) => void,
 *   onChoose: (files: import('services/ipaws/tenants/femaTenants.service').FemaCredentialFiles) => void,
 * }} props
 */
const FileStep = ({ keyAlias, onKeyAliasChange, onChoose }) => {
  const [kind, setKind] = useState('keystore');
  const [keystore, setKeystore] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [key, setKey] = useState(null);
  const [chain, setChain] = useState(null);

  const ready = kind === 'keystore' ? Boolean(keystore) : Boolean(certificate && key);

  const submit = (event) => {
    event.preventDefault();
    if (!ready) return;
    onChoose(
      kind === 'keystore'
        ? { kind: 'keystore', keystore }
        : { kind: 'pem', certificate, key, chain },
    );
  };

  return (
    <Form onSubmit={submit} autoComplete="off">
      <fieldset className="mb-3">
        <legend className="h6">Credential format</legend>
        <Form.Check
          type="radio"
          id="fema-credential-kind-keystore"
          name="fema-credential-kind"
          label="Keystore file from FEMA (.jks)"
          checked={kind === 'keystore'}
          onChange={() => setKind('keystore')}
        />
        <Form.Check
          type="radio"
          id="fema-credential-kind-pem"
          name="fema-credential-kind"
          label="PEM files (certificate, private key, optional chain)"
          checked={kind === 'pem'}
          onChange={() => setKind('pem')}
        />
      </fieldset>

      {kind === 'keystore' ? (
        <>
          <Form.Group className="mb-3" controlId="fema-credential-keystore">
            <Form.Label>Keystore file</Form.Label>
            <Form.Control
              type="file"
              accept=".jks"
              onChange={(event) => setKeystore(firstFile(event))}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="fema-credential-key-alias">
            <Form.Label>Key alias (optional)</Form.Label>
            <Form.Control
              type="text"
              value={keyAlias}
              maxLength={128}
              onChange={(event) => onKeyAliasChange(event.target.value)}
            />
            <Form.Text>Leave this empty when the keystore holds one key.</Form.Text>
          </Form.Group>
        </>
      ) : (
        <>
          <Form.Group className="mb-3" controlId="fema-credential-certificate">
            <Form.Label>Certificate file</Form.Label>
            <Form.Control
              type="file"
              accept=".pem,.crt,.cer"
              onChange={(event) => setCertificate(firstFile(event))}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="fema-credential-key">
            <Form.Label>Private-key file</Form.Label>
            <Form.Control
              type="file"
              accept=".pem,.key"
              onChange={(event) => setKey(firstFile(event))}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="fema-credential-chain">
            <Form.Label>Chain file (optional)</Form.Label>
            <Form.Control
              type="file"
              accept=".pem,.crt,.cer"
              onChange={(event) => setChain(firstFile(event))}
            />
          </Form.Group>
        </>
      )}

      <Button type="submit" disabled={!ready}>
        Continue
      </Button>
    </Form>
  );
};

export default FileStep;
